'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@components/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@components/components/ui/dropdown-menu';
import { cn } from '@components/lib/utils';
import { explorerAnalytics } from '@/lib/analytics';
import { pendoTrack } from '@/lib/pendo/trackPendoEvent';
import { EVENT_SOURCES } from '@/constant/eventSources';
import {
  EVENT_TIME_RANGES,
  getTimeRangeLabel,
} from '@/constant/eventTimeRanges';
import {
  AUTO_FORWARD_ENABLED_KEY,
  FORWARD_MODE_KEY,
  PROXY_URL_KEY,
  TARGET_URL_KEY,
  type ForwardMode,
} from '@/constant/eventForwardSamples';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
// TODO: Re-enable when sort/auto-forward/auto-refresh controls are restored
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipProvider,
//   TooltipTrigger,
// } from '@radix-ui/react-tooltip';
import {
  // ArrowDownAZ,
  // ArrowUpAZ,
  ChevronDown,
  // Info,
  ListFilter,
  RefreshCw,
  Search,
  // Settings,
  X,
  // Zap,
} from 'lucide-react';
import {
  EventForwardConfigModal,
  type ForwardConfig,
} from './EventForwardConfigModal';

interface EventFilterBarProps {
  eventCount: number;
  onRefresh: () => void;
  onPollRefresh: () => Promise<void>;
}

export const EventFilterBar: React.FC<EventFilterBarProps> = ({
  eventCount,
  onRefresh,
  onPollRefresh,
}) => {
  const {
    filters,
    // sortDirection,
    setFilters,
    // setSortDirection,
    pollingEnabled,
    // setPollingEnabled,
    // autoForwardEnabled,
    // autoForwardStats,
    setAutoForwardEnabled,
    setForwardConfig,
  } = useEventExplorerStore();

  const [searchInput, setSearchInput] = useState(filters.searchQuery);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [refreshPhase, setRefreshPhase] = useState<
    'countdown' | 'refreshing' | 'refreshed'
  >('countdown');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isMountedRef = useRef(true);
  const onPollRefreshRef = useRef(onPollRefresh);

  // Keep ref in sync without re-triggering effects
  useEffect(() => {
    onPollRefreshRef.current = onPollRefresh;
  }, [onPollRefresh]);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Load auto-forward state from localStorage on mount (only if config is valid)
  useEffect(() => {
    const saved = localStorage.getItem(AUTO_FORWARD_ENABLED_KEY);
    const hasTarget = !!localStorage.getItem(TARGET_URL_KEY);
    if (saved === 'true' && hasTarget) {
      setAutoForwardEnabled(true);
    }
  }, [setAutoForwardEnabled]);

  // TODO: Re-enable when auto-forward toggle is restored
  // const handleAutoForwardToggle = useCallback(() => {
  //   if (autoForwardEnabled) {
  //     setAutoForwardEnabled(false);
  //     localStorage.setItem(AUTO_FORWARD_ENABLED_KEY, 'false');
  //     return;
  //   }
  //   const targetUrl = localStorage.getItem(TARGET_URL_KEY);
  //   if (!targetUrl) {
  //     setConfigModalOpen(true);
  //     return;
  //   }
  //   setAutoForwardEnabled(true);
  //   localStorage.setItem(AUTO_FORWARD_ENABLED_KEY, 'true');
  // }, [autoForwardEnabled, setAutoForwardEnabled]);

  const handleConfigSave = useCallback(
    (config: ForwardConfig) => {
      // Persist to localStorage
      localStorage.setItem(FORWARD_MODE_KEY, config.mode);
      localStorage.setItem(PROXY_URL_KEY, config.proxyUrl);
      localStorage.setItem(TARGET_URL_KEY, config.targetUrl);

      // Update store so EventForwardTab picks up changes reactively
      setForwardConfig(config);

      // Enable auto-forward since user came here to set it up
      setAutoForwardEnabled(true);
      localStorage.setItem(AUTO_FORWARD_ENABLED_KEY, 'true');

      pendoTrack('event_auto_forward_toggled', {
        enabled: true,
        forward_mode: config.mode,
      });
    },
    [setAutoForwardEnabled, setForwardConfig]
  );

  // ── Refresh phase state machine ──────────────────────────────────────
  //
  // Cycle: 'countdown' (5-4-3-2-1-0) → call poll → 'refreshing' (while fetching)
  //        → 'refreshed' (300ms) → 'countdown' (restart from 5)
  //
  // The UI countdown drives when the poll fires (no setInterval in the hook).

  // 1. Local countdown timer (runs only during 'countdown' phase, always starts from 5)
  useEffect(() => {
    if (refreshPhase !== 'countdown' || !pollingEnabled) {
      setCountdown(null);
      return;
    }
    setCountdown(5);
    const timer = setInterval(() => {
      setCountdown((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [refreshPhase, pollingEnabled]);

  // 2. When countdown hits 0 → fire the poll and enter 'refreshing'
  useEffect(() => {
    if (countdown !== 0 || refreshPhase !== 'countdown') return;
    setRefreshPhase('refreshing');
    onPollRefreshRef.current();
  }, [countdown, refreshPhase]);

  // 3. 'refreshing' → 'refreshed' after 800ms (guaranteed transition via effect cleanup)
  useEffect(() => {
    if (refreshPhase !== 'refreshing') return;
    const timeout = setTimeout(() => setRefreshPhase('refreshed'), 800);
    return () => clearTimeout(timeout);
  }, [refreshPhase]);

  // 4. 'refreshed' → 'countdown' after 500ms
  useEffect(() => {
    if (refreshPhase !== 'refreshed') return;
    const timeout = setTimeout(() => setRefreshPhase('countdown'), 500);
    return () => clearTimeout(timeout);
  }, [refreshPhase]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setFilters({ searchQuery: value });
        if (value) {
          explorerAnalytics.eventSearchPerformed({ searchQuery: value });
        }
      }, 300);
    },
    [setFilters]
  );

  // Cleanup debounce timer
  useEffect(() => {
    return () => clearTimeout(searchTimerRef.current);
  }, []);

  const toggleSource = (source: string) => {
    const current = filters.source;
    const next = current.includes(source)
      ? current.filter((s) => s !== source)
      : [...current, source];
    setFilters({ source: next });
    explorerAnalytics.eventFilterApplied({
      filterType: 'source',
      filterValue: source,
    });
  };

  const sourceLabel =
    filters.source.length === 0
      ? 'All sources'
      : filters.source.length === 1
        ? (EVENT_SOURCES.find((s) => s.value === filters.source[0])?.label ??
          filters.source[0])
        : `${filters.source.length} sources`;

  return (
    <div className="relative z-10 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
      {/* Top row: source filter, refresh, count */}
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-3">
          {/* Source dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {sourceLabel}
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {EVENT_SOURCES.map((source) => (
                <DropdownMenuCheckboxItem
                  key={source.value}
                  checked={filters.source.includes(source.value)}
                  onCheckedChange={() => toggleSource(source.value)}
                  onSelect={(e) => e.preventDefault()}
                  className={cn(
                    filters.source.includes(source.value)
                      ? 'text-shade-teal-700 font-medium'
                      : 'text-gray-600'
                  )}
                >
                  {source.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Time range dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ListFilter className="h-3.5 w-3.5" />
                {getTimeRangeLabel(filters.timeRangePreset)}
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuRadioGroup
                value={filters.timeRangePreset}
                onValueChange={(value) => {
                  setFilters({
                    timeRangePreset: value,
                    dateFrom: null,
                    dateTo: null,
                  });
                  explorerAnalytics.eventFilterApplied({
                    filterType: 'timeRange',
                    filterValue: value,
                  });
                }}
              >
                {EVENT_TIME_RANGES.map((range) => (
                  <DropdownMenuRadioItem
                    key={range.value}
                    value={range.value}
                    className={cn(
                      filters.timeRangePreset === range.value
                        ? 'text-shade-teal-700 font-medium'
                        : 'text-gray-600'
                    )}
                  >
                    {range.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right: polling status + refresh */}
        <div className="flex items-center gap-3">
          {/* Polling status indicator: 3 phases */}
          {pollingEnabled && (
            <span className="flex items-center gap-1.5 text-xs">
              {refreshPhase === 'refreshing' ? (
                <>
                  <RefreshCw className="h-3 w-3 text-amber-500 animate-spin" />
                  <span className="text-amber-600">Refreshing…</span>
                </>
              ) : refreshPhase === 'refreshed' ? (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span className="text-green-600">Refreshed!</span>
                </>
              ) : countdown != null ? (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-blue-600">
                    Refreshing in {countdown}s
                  </span>
                </>
              ) : null}
            </span>
          )}

          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              explorerAnalytics.eventListRefreshed({ isManual: true });
              setRefreshPhase('refreshing');
              onRefresh();
              // After manual refresh completes, show "Refreshed!" then restart
              // onRefresh (fetchEvents) is sync but triggers state updates,
              // so use a short delay to let the spinner show
              setTimeout(() => {
                if (isMountedRef.current) setRefreshPhase('refreshed');
              }, 500);
            }}
            title="Refresh events"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bottom row: search, event count */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
        <div className="flex items-center gap-2 flex-1">
          {/* Search input */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by event ID..."
              className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus:border-shade-teal-300 focus:outline-none focus:ring-1 focus:ring-shade-teal-300"
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleSearchChange('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
              >
                <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
              </Button>
            )}
          </div>
        </div>

        <span className="text-xs text-gray-400">
          Showing {eventCount} events
        </span>

        {/* TODO: Re-enable these controls when ready
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
            }
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {sortDirection === 'desc' ? (
              <ArrowDownAZ className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpAZ className="h-3.5 w-3.5" />
            )}
            {sortDirection === 'desc' ? 'By latest' : 'By oldest'}
          </Button>

          <div className="h-4 w-px bg-gray-200" />

          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAutoForwardToggle}
              title="Automatically forward new events to your configured endpoint as they arrive"
              className={cn(
                'rounded-full px-2.5 py-1 text-xs',
                autoForwardEnabled
                  ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              )}
            >
              <Zap
                className={cn(
                  'h-3 w-3',
                  autoForwardEnabled ? 'text-amber-500' : 'text-gray-400'
                )}
              />
              Auto-forward
              {autoForwardStats.total > 0 && (
                <span
                  className={cn(
                    'ml-0.5 tabular-nums',
                    autoForwardStats.failed > 0
                      ? 'text-red-500'
                      : 'text-amber-500'
                  )}
                >
                  {autoForwardStats.succeeded}/{autoForwardStats.total}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setConfigModalOpen(true)}
              className="h-6 w-6 text-gray-400 hover:text-gray-600"
              title="Forward configuration"
            >
              <Settings className="h-3 w-3" />
            </Button>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-gray-300 hover:text-gray-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="bg-gray-800 text-white px-3 py-2 rounded-md text-xs max-w-64 leading-relaxed"
                >
                  When enabled, new events are automatically forwarded to your
                  target endpoint as they arrive. Use ⚙ to configure the
                  target. Auto-disables after 10 consecutive failures.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="h-4 w-px bg-gray-200" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPollingEnabled(!pollingEnabled)}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs',
              pollingEnabled
                ? 'bg-green-50 text-green-600 hover:bg-green-100'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                pollingEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              )}
            />
            {pollingEnabled ? 'Auto-refresh' : 'Paused'}
            {pollingEnabled && countdown != null && (
              <span className="ml-0.5 tabular-nums text-green-500">
                {countdown}s
              </span>
            )}
          </Button>
        </div>
        */}
      </div>

      {/* Config modal (triggered when auto-forward clicked without config) */}
      <EventForwardConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        onSave={handleConfigSave}
        initialConfig={{
          mode:
            (localStorage.getItem(FORWARD_MODE_KEY) as ForwardMode) ?? 'proxy',
          proxyUrl: localStorage.getItem(PROXY_URL_KEY) ?? '',
          targetUrl: localStorage.getItem(TARGET_URL_KEY) ?? '',
        }}
      />
    </div>
  );
};
